// CTV-682: the market a sheet names in copy ("the areas of Honolulu you want
// to reach", "Local to Honolulu").
//
// Nielsen DMA names are much messier than "City, ST". Real examples from our
// own stations, and what this produces:
//
//   Honolulu, HI                                   -> Honolulu
//   Miami-Fort Lauderdale, FL                      -> Miami-Fort Lauderdale
//   Raleigh-Durham (Fayetteville), NC              -> Raleigh-Durham
//   Cedar Rapids-Waterloo-Iowa City & Dubuque, IA  -> Cedar Rapids-Waterloo
//   Rochester, MN-Mason City, IA-Austin, MN        -> Rochester-Mason City
//   United States (National)                       -> (none)
//
// Names too long for the sheet are shortened to their first two cities, which
// is how these markets are spoken about anyway. That does drop trailing
// cities, so it's a deliberate trade: a station in Dubuque appears under
// "Cedar Rapids-Waterloo" rather than the sheet naming no place at all.
//
// A null return means the caller falls back to generic wording, which every
// call site already handles.

import type { PublisherConfig } from '../../../types/selfService';

/**
 * Longest label the sheet carries without the step-1 sentence and the
 * locality chip running long. Sized from the real DMA list: it keeps
 * "Miami-Fort Lauderdale" and "Wilkes Barre-Scranton" (21) whole.
 */
export const MAX_MARKET_LABEL = 22;

/** Nielsen's national pseudo-market, which isn't a place. */
const NATIONAL = /united states|\bnational\b/i;

/**
 * Split a DMA name into its city parts, with state codes and parentheticals
 * removed from each.
 *
 * State codes are stripped per-part rather than only from the end: several
 * names carry them mid-string ("Rochester, MN-Mason City, IA-Austin, MN"),
 * and an end-only strip leaves them embedded in the label.
 */
export function dmaCities(dmaName: string): string[] {
  return dmaName
    .replace(/\s*\([^)]*\)/g, '')
    .split('-')
    .map((part) => part.replace(/,\s*[A-Za-z]{2}\s*$/, '').trim())
    .filter(Boolean);
}

/**
 * The market label for this station, shortened if needed, or null when there
 * isn't one that reads well.
 */
export function marketCity(
  config: Pick<PublisherConfig, 'markets' | 'coverage'>,
): string | null {
  if (config.coverage === 'national') return null;

  const markets = config.markets ?? [];
  if (markets.length !== 1) return null;

  const cities = dmaCities(markets[0]?.dmaName ?? '');
  if (cities.length === 0) return null;
  if (NATIONAL.test(cities.join(' '))) return null;

  // Whole name, then the first two cities, then the first — first one that fits.
  for (const take of [cities.length, 2, 1]) {
    const label = cities.slice(0, take).join('-');
    if (label.length <= MAX_MARKET_LABEL) return label;
  }
  return null;
}
